// src/components/FireworksShow.jsx
import React, { useEffect, useRef } from 'react';

export default function FireworksShow({
  active = true,
  rocketRateMs = 700,
  spinnerRateMs = 3200,
  fountainRateMs = 2800,
  intensity = 1.0,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  // simulation state
  const rockets = useRef([]);
  const particles = useRef([]);
  const fountains = useRef([]);
  const spinners = useRef([]);

  // time-based spawner accumulators
  const tSpawn = useRef({ r: 0, s: 0, f: 0 });

  useEffect(() => {
    if (!active) return;

    const prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    // ------- Canvas -------
    const canvas = document.createElement('canvas');
    canvasRef.current = canvas;
    Object.assign(canvas.style, {
      position: 'fixed',
      inset: '0',
      width: '100vw',
      height: '100vh',
      zIndex: 10,
      pointerEvents: 'none',
    });
    document.body.appendChild(canvas);

    const ctx = canvas.getContext('2d', { alpha: true });

    // ------- Constants / Tunables -------
    const COLORS = ['#ffd966', '#ffb347', '#ff8844', '#ff4d4d', '#fff1a8', '#ffffff'];

    // Physics (base per ~60fps frame; scaled by dt)
    const GRAVITY = 0.06;
    const ROCKET_GRAVITY_SCALE = 0.25;
    const DRAG = 0.985;

    // Performance/quality controls
    const DPR_CAP = 1.5;
    const PARTICLE_BUDGET_BASE = 1600;
    const MAX_ROCKETS = 8;
    const MAX_SPINNERS = 10;

    // Adaptive emission scaler
    let adaptiveScale = 1.0; // 1..0.55
    const adapt = (dtMs) => {
      const target = 16.7; // 60fps
      const ratio = Math.min(2, Math.max(0.5, target / Math.max(8, dtMs)));
      adaptiveScale = adaptiveScale * 0.9 + (Math.min(1, ratio) * 0.1);
      adaptiveScale = Math.min(1, Math.max(0.55, adaptiveScale));
    };

    // DPR sizing
    const dprResize = () => {
      const dpr = Math.min(DPR_CAP, Math.max(1, window.devicePixelRatio || 1));
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    dprResize();
    window.addEventListener('resize', dprResize);

    const rand = (a, b) => Math.random() * (b - a) + a;
    const pick = (arr) => arr[(Math.random() * arr.length) | 0];
    const randi = (a, b) => (Math.random() * (b - a + 1) + a) | 0;

    // ------- Spawners -------
    function spawnRocket() {
      if (rockets.current.length >= MAX_ROCKETS) return;
      const w = window.innerWidth, h = window.innerHeight;
      rockets.current.push({
        x: rand(w * 0.12, w * 0.88),
        y: h + 8,
        vx: (Math.random() - 0.5) * 1.0,
        vy: -(7.3 + Math.random() * 2.1),
        targetY: rand(h * 0.18, h * 0.32),
        color: pick(COLORS),
        // multi-break
        bursting: false,
        breaksLeft: 0,
        breakTimer: 0, // ms until next burst when bursting
        age: 0,        // ms since launch
      });
    }

    function reseedRocket(r) {
      const w = window.innerWidth, h = window.innerHeight;
      r.x = rand(w * 0.12, w * 0.88);
      r.y = h + 8;
      r.vx = (Math.random() - 0.5) * 1.0;
      r.vy = -(7.3 + Math.random() * 2.1);
      r.targetY = rand(h * 0.18, h * 0.32);
      r.color = pick(COLORS);
      r.bursting = false;
      r.breaksLeft = 0;
      r.breakTimer = 0;
      r.age = 0;
    }

    function spawnSpinner() {
      if (spinners.current.length >= MAX_SPINNERS) return;
      const w = window.innerWidth, h = window.innerHeight;
      spinners.current.push({
        x: rand(w * 0.08, w * 0.92),
        y: h - rand(18, 34),
        life: 520 + (Math.random() * 520) | 0,
        angle: Math.random() * Math.PI * 2,
        speed: 0.35 + Math.random() * 0.45,
        color: pick(COLORS),
      });
    }

    function initFountains() {
      const h = window.innerHeight;
      fountains.current = [
        { x: 24, y: h - 16, hue: 38, tick: 0, life: 1e9 },
        { x: window.innerWidth - 24, y: h - 16, hue: 50, tick: 0, life: 1e9 },
      ];
    }
    initFountains();

    function explode(x, y, color) {
      const budget = Math.floor((70 + Math.random() * 50) * intensity * adaptiveScale);
      const particleBudget = Math.floor(PARTICLE_BUDGET_BASE * adaptiveScale);
      if (particles.current.length > particleBudget) return;

      for (let i = 0; i < budget; i++) {
        const a = Math.random() * Math.PI * 2;
        const s = 2.6 + Math.random() * 5.0;
        particles.current.push({
          kind: 0, // burst
          x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
          life: 65 + (Math.random() * 45) | 0,
          color,
          size: 1.4 + Math.random() * 2.6,
          flicker: true,
        });
      }
      const twinkle = Math.floor(22 * intensity * adaptiveScale);
      for (let i = 0; i < twinkle; i++) {
        particles.current.push({
          kind: 0,
          x, y, vx: (Math.random() - 0.5) * 1.6, vy: (Math.random() - 0.5) * 1.6,
          life: 24 + (Math.random() * 20) | 0,
          color: '#ffffff',
          size: 1.0 + Math.random() * 1.6,
          flicker: true,
        });
      }
    }

    function emitFountain(f) {
      const sprays = Math.floor((12 + Math.random() * 8) * intensity * adaptiveScale);
      for (let i = 0; i < sprays; i++) {
        const angle = (-Math.PI / 2) + rand(-0.33, 0.33) + (f.x < window.innerWidth / 2 ? 0.08 : -0.08);
        const speed = 2.4 + Math.random() * 2.2;
        const col = `hsl(${f.hue + rand(-8, 8)}, 90%, ${60 + Math.random() * 20}%)`;
        particles.current.push({
          kind: 1, // fountain
          x: f.x, y: f.y,
          vx: Math.cos(angle) * speed * 0.9,
          vy: Math.sin(angle) * speed * 1.15,
          life: 40 + (Math.random() * 24) | 0,
          color: col,
          size: 0.9 + Math.random() * 1.8,
          flicker: true,
        });
      }
      particles.current.push({
        kind: 2, x: f.x, y: f.y, vx: 0, vy: -0.15, life: 18,
        color: 'rgba(255,190,90,0.5)', size: 2.0, flicker: false,
      });
    }

    function emitSpinner(s) {
      const emits = Math.max(16, Math.floor(20 * intensity * adaptiveScale));
      for (let i = 0; i < emits; i++) {
        const a = s.angle + (i / emits) * Math.PI * 2;
        const speed = 1.9 + Math.random() * 1.9;
        particles.current.push({
          kind: 3, // wheel
          x: s.x, y: s.y,
          vx: Math.cos(a) * speed,
          vy: Math.sin(a) * speed * 0.58,
          life: 28 + (Math.random() * 22) | 0,
          color: s.color,
          size: 0.9 + Math.random() * 1.8,
          flicker: true,
        });
      }
    }

    // ------- Drawing helpers -------
    const clear = () => {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = 'rgba(13, 26, 38, 0.18)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    };

    const drawRocket = (r) => {
      // tail
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 0.8;
      ctx.strokeStyle = 'rgba(255,220,150,0.6)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(r.x, r.y + 10);
      ctx.lineTo(r.x, r.y - 2);
      ctx.stroke();

      // head (additive)
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = r.color;
      ctx.beginPath(); ctx.arc(r.x, r.y, 2.8, 0, Math.PI * 2); ctx.fill();

      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    // Per-frame → time-scaled physics helpers
    const applyDragPow = (pow) => Math.pow(DRAG, pow);

    const stepParticle = (p, df) => {
      const dragPow = applyDragPow(df);
      p.x += p.vx * df;
      p.y += p.vy * df;

      if (p.kind === 3) { // wheel
        p.vx *= dragPow; p.vy *= dragPow; p.vy += GRAVITY * 0.02 * df;
      } else if (p.kind === 1) { // fountain
        p.vx *= dragPow * 0.985; p.vy = p.vy * (dragPow * 0.975) + GRAVITY * 0.03 * df;
      } else { // burst/glow
        p.vx *= dragPow; p.vy = p.vy * dragPow + GRAVITY * df;
      }

      p.life -= df; // scale life by df so duration is consistent
      return p.life > 0;
    };

    const drawParticles = (df) => {
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i < particles.current.length; i++) {
        const p = particles.current[i];
        const alive = stepParticle(p, df);
        if (!alive) {
          particles.current[i] = particles.current[particles.current.length - 1];
          particles.current.pop();
          i--;
          continue;
        }
        const alpha = Math.max(0, Math.min(1, p.life / 28)) * (p.flicker ? (0.85 + Math.random() * 0.4) : 1);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    };

    // ------- Main loop -------
    let last = performance.now();

    const tick = (now) => {
      const dt = now - last;             // ms since last frame
      last = now;
      adapt(dt);

      // Normalize dt to "frames" assuming 60fps for our original tuning.
      const df = Math.max(0.5, Math.min(2.5, dt / 16.67)); // ~0.5x..2.5x frame

      clear();

      // rockets (guaranteed burst logic)
      const skyTopY = window.innerHeight * 0.10; // hard top threshold (10% from top)
      const maxFlightMs = 4200;                  // absolute failsafe

      for (let i = 0; i < rockets.current.length; i++) {
        const r = rockets.current[i];
        r.age += dt;

        // physics (time-scaled)
        r.x += r.vx * df;
        r.y += r.vy * df;
        r.vy += GRAVITY * ROCKET_GRAVITY_SCALE * df;

        drawRocket(r);

        // If currently in multi-break sequence
        if (r.bursting) {
          r.breakTimer -= dt;
          if (r.breakTimer <= 0 && r.breaksLeft > 0) {
            explode(r.x, r.y, r.color);
            r.breaksLeft--;
            if (r.breaksLeft > 0) {
              r.breakTimer = rand(120, 320);
              r.vx += rand(-0.25, 0.25);
              r.vy += rand(-0.4, 0.2);
            } else {
              r.bursting = false;
              reseedRocket(r); // sequence complete → relaunch
            }
          }
          // safety: if falls off screen during bursting, relaunch
          if (r.y >= window.innerHeight + 60) reseedRocket(r);
          continue;
        }

        // --- GUARANTEED BURST TRIGGERS ---
        const reachedTarget = r.y <= r.targetY;               // intended burst window
        const passedApex   = r.vy >= 0;                        // velocity switched (apex or after)
        const tooOld       = r.age > maxFlightMs;              // max time
        const crossedTop   = r.y <= skyTopY;                   // hard top-of-sky clamp

        if (reachedTarget || passedApex || tooOld || crossedTop) {
          explode(r.x, r.y, r.color);

          // Random multi-breaks for variety
          if (Math.random() < 0.65) {
            r.bursting = true;
            r.breaksLeft = randi(1, 3);
            r.breakTimer = rand(140, 320);
            r.vy *= 0.2;                 // cluster near apex
            r.vx += rand(-0.2, 0.2);
          } else {
            reseedRocket(r);             // single burst → relaunch
          }
          continue;
        }

        // If somehow escapes downward, relaunch
        if (r.y >= window.innerHeight + 60) reseedRocket(r);
      }

      // fountains
      for (let i = 0; i < fountains.current.length; i++) {
        const f = fountains.current[i];
        f.tick++;
        if (f.tick % 3 === 0) emitFountain(f);
        ctx.fillStyle = 'rgba(255,190,90,0.22)';
        ctx.beginPath(); ctx.arc(f.x, f.y + 2, 2.0, 0, Math.PI * 2); ctx.fill();
      }

      // spinners
      for (let i = 0; i < spinners.current.length; i++) {
        const s = spinners.current[i];
        s.angle += s.speed * df;
        emitSpinner(s);
        s.life -= df;
        ctx.fillStyle = 'rgba(255,220,120,0.22)';
        ctx.beginPath(); ctx.arc(s.x, s.y, 2, 0, Math.PI * 2); ctx.fill();
        if (s.life <= 0) {
          spinners.current[i] = spinners.current[spinners.current.length - 1];
          spinners.current.pop();
          i--;
        }
      }

      // particles
      drawParticles(df);

      // ---- time-based spawning ----
      const scale = adaptiveScale;
      const rMs = rocketRateMs / Math.max(0.3, scale);
      const sMs = spinnerRateMs / Math.max(0.5, scale);
      const fMs = fountainRateMs / Math.max(0.6, scale);

      tSpawn.current.r += dt;
      tSpawn.current.s += dt;
      tSpawn.current.f += dt;

      while (tSpawn.current.r >= rMs) {
        if (rockets.current.length < MAX_ROCKETS) {
          spawnRocket();
          if (Math.random() < 0.55 && rockets.current.length < MAX_ROCKETS) spawnRocket();
        }
        tSpawn.current.r -= rMs;
      }
      while (tSpawn.current.s >= sMs) {
        spawnSpinner();
        tSpawn.current.s -= sMs;
      }
      while (tSpawn.current.f >= fMs) {
        if (fountains.current.length !== 2) initFountains();
        tSpawn.current.f -= fMs;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const start = () => {
      if (!rafRef.current) {
        last = performance.now();
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    const stop = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };

    const onVis = () => (document.hidden ? stop() : start());
    document.addEventListener('visibilitychange', onVis);

    start();

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stop();
      window.removeEventListener('resize', dprResize);
      rockets.current = [];
      particles.current = [];
      fountains.current = [];
      spinners.current = [];
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      canvasRef.current = null;
    };
  }, [active, rocketRateMs, spinnerRateMs, fountainRateMs, intensity]);

  return null;
}
